/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useState } from 'react';

import { useIntl, type MessageDescriptor } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Field } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives/compiled';
import SmartUserPicker, { type OptionData, type User } from '@atlaskit/smart-user-picker';
import { token } from '@atlaskit/tokens';

import { resourceIdFromIdentityAri } from '../../atlassian-refs';
import type { FocusAskUserData } from '../../types';

type Props = {
	name: string;
	label: MessageDescriptor;
	placeholder: MessageDescriptor;
	cloudId: string;
	value: FocusAskUserData | null;
	onChange: (user: FocusAskUserData | null) => void;
	isRequired?: boolean;
	fieldId: string;
	isDisabled?: boolean;
	isClearable?: boolean;
	hideLabel?: boolean;
	isCompact?: boolean;
	width?: number | string;
	appearance?: 'default' | 'subtle';
};

function isUserOption(option: OptionData | undefined | null): option is User {
	return option?.type === 'user' || option?.type === 'external_user';
}

function optionToFocusAskUser(option: OptionData | undefined | null): FocusAskUserData | null {
	if (!option || !isUserOption(option)) {
		return null;
	}
	const id = resourceIdFromIdentityAri(option.id, 'user');
	const name = option.name?.trim() ? option.name.trim() : id;
	const avatarUrl = option.avatarUrl;
	return avatarUrl ? { id, name, avatarUrl } : { id, name };
}

const styles = cssMap({
	compactSubtleControl: {
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

const COMPACT_CONTROL_HEIGHT = '2.5rem';

export const UserPickerField = ({
	name,
	label,
	placeholder,
	cloudId,
	value,
	onChange,
	isRequired,
	fieldId,
	isDisabled = false,
	isClearable = true,
	hideLabel = false,
	isCompact = false,
	width = '100%',
	appearance,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const labelText = formatMessage(label);

	const pickerUserId = value ? resourceIdFromIdentityAri(value.id, 'user') : undefined;

	const [selectedUser, setSelectedUser] = useState<OptionData | undefined>(undefined);
	const pickerKey = `${fieldId}-${pickerUserId ?? 'empty'}`;

	useEffect(() => {
		setSelectedUser(undefined);
	}, [pickerUserId]);

	const smartUserPicker = (
		<SmartUserPicker
			key={pickerKey}
			fieldId={fieldId}
			defaultValue={pickerUserId ? { id: pickerUserId, type: 'user' } : undefined}
			isClearable={isClearable}
			isDisabled={isDisabled}
			placeholder={formatMessage(placeholder)}
			prefetch
			productKey="people"
			siteId={cloudId}
			width={width}
			height={isCompact ? COMPACT_CONTROL_HEIGHT : undefined}
			minHeight={isCompact ? COMPACT_CONTROL_HEIGHT : undefined}
			noBorder={isCompact}
			subtle={appearance === 'subtle'}
			ariaLabel={hideLabel ? labelText : undefined}
			required={isRequired}
			includeGroups={false}
			includeTeams={false}
			value={selectedUser}
			onChange={(newValue) => {
				const next = newValue as OptionData | undefined;
				setSelectedUser(next && isUserOption(next) ? next : undefined);
				onChange(optionToFocusAskUser(next ?? null));
			}}
			maxOptions={10}
		/>
	);

	const picker =
		isCompact && appearance === 'subtle' && !isDisabled ? (
			<Box xcss={styles.compactSubtleControl}>{smartUserPicker}</Box>
		) : (
			smartUserPicker
		);

	if (hideLabel) {
		return picker;
	}

	return (
		<Field name={name} label={labelText} isRequired={isRequired}>
			{() => picker}
		</Field>
	);
};
